import React, { useEffect, useState } from "react";

import styles from "./courseRiskHeatmap.module.css";
import FinanceCardChart from "../financeCardChart/financeCardChart";
import ZeroData from "../../zeroData/zeroData";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import { formatNumberToCurrency } from "../../../utils/formatter";
import { financeCourseRiskHeatmap, financeCourseRiskDetails } from "../../../services/finance";

// Rampa sequencial de hue única (vermelho), 5 passos claro→escuro.
// Validada com o validador da skill dataviz em modo --ordinal: lightness monótona,
// ΔL adjacente ≥ 0.06, ponta clara a 2.07:1 sobre a superfície do card. Não trocar
// passos isolados sem revalidar.
const RAMP = ["#EA9E9E", "#DC7676", "#C95050", "#AF3838", "#8E2828"];
// Tinta por passo: contraste ≥ 4.5:1 em todos (7.76 / 5.41 / 4.42 / 6.09 / 8.45).
// Não dá para usar o azul da plataforma aqui — sobre a rampa ele fica em 1.4–2.9.
const RAMP_INK = ["#1F1F1F", "#1F1F1F", "#FFFFFF", "#FFFFFF", "#FFFFFF"];
const ZERO_FILL = "#F4F4F5";
// Cinza médio (4.85:1 sobre o preenchimento) em vez de quase-preto: zero não precisa de peso.
const ZERO_INK = "#6B6B6B";

// Intensidade relativa ao maior valor da própria coluna — as duas colunas contam
// coisas diferentes e não se comparam entre si.
const cellStyle = (count, max) => {
  if (!count) {
    return { backgroundColor: ZERO_FILL, color: ZERO_INK };
  }
  const step = Math.min(RAMP.length - 1, Math.max(0, Math.ceil((count / max) * RAMP.length) - 1));
  return { backgroundColor: RAMP[step], color: RAMP_INK[step] };
};

const formatStamp = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString("pt-BR");
};

// Os dois lados vêm de pipelines diferentes e cada um tem o seu corte de risco — por isso
// o cabeçalho declara qual é, em vez de deixar "Evasão"/"Inadimplência" ambíguos.
const COLUMNS = [
  { key: "dropoutCount", risk: "dropout", label: "Evasão", cut: "score ≥ 3", noun: "evasão" },
  { key: "delinquencyCount", risk: "delinquency", label: "Inadimplência", cut: "prob ≥ 60%", noun: "inadimplência" }
];

const num = (value, digits = 1) => (value == null ? "-" : Number(value).toFixed(digits));

// Colunas do detalhamento por tipo de risco: os dois pipelines guardam campos diferentes,
// e só "Motivos" é comum (ReasonsJson do lado da evasão, reason_text dos fatores do outro).
const DETAIL_COLUMNS = {
  dropout: [
    { label: "Aluno", render: (s) => s.studentId },
    { label: "Score", render: (s) => s.riskScore ?? "-" },
    { label: "Frequência", render: (s) => (s.avgFrequency == null ? "-" : `${num(s.avgFrequency)}%`) },
    { label: "Nota final", render: (s) => num(s.avgFinalGrade) },
    { label: "Parcelas vencidas", render: (s) => s.overdueInstallments ?? "-" },
    { label: "Valor vencido", render: (s) => (s.overdueValue == null ? "-" : formatNumberToCurrency(s.overdueValue)) }
  ],
  delinquency: [
    { label: "Aluno", render: (s) => s.studentId },
    { label: "Probabilidade", render: (s) => (s.probability == null ? "-" : `${num(s.probability * 100)}%`) },
    { label: "Faixa", render: (s) => s.riskBand ?? "-" },
    { label: "Pontuado em", render: (s) => formatStamp(s.analyzedAt) ?? "-" }
  ]
};

const CourseRiskHeatmap = ({ institutionId, code }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [sort, setSort] = useState({ key: "total", dir: "desc" });
  // Célula selecionada: { courseName, risk, label, count }. null = nenhuma tabela aberta.
  const [drill, setDrill] = useState(null);
  const [drillRows, setDrillRows] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => {
    if (!institutionId) {
      setData(null);
      return;
    }

    // Descarta a resposta se a instituição mudar antes dela chegar.
    let active = true;
    (async () => {
      try {
        setIsLoading(true);
        const response = await financeCourseRiskHeatmap({ institutionId });
        if (active) {
          setData(response);
        }
      } catch (error) {
        console.log("Erro ao buscar o mapa de calor de risco por curso", error);
        if (active) {
          setData(null);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [institutionId]);

  // Fecha a tabela ao trocar de instituição — os alunos listados eram de outra base.
  useEffect(() => {
    setDrill(null);
  }, [institutionId]);

  useEffect(() => {
    if (!drill) {
      setDrillRows([]);
      return;
    }

    // Descarta a resposta se outra célula for clicada antes dela chegar.
    let active = true;
    (async () => {
      try {
        setDrillLoading(true);
        const students = await financeCourseRiskDetails({
          institutionId,
          risk: drill.risk,
          courseName: drill.courseName
        });
        if (active) {
          setDrillRows(students);
        }
      } catch (error) {
        console.log("Erro ao buscar o detalhamento de risco do curso", error);
        if (active) {
          setDrillRows([]);
        }
      } finally {
        if (active) {
          setDrillLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [drill, institutionId]);

  const toggleSort = (key) => {
    setSort((current) => (current.key === key
      ? { key, dir: current.dir === "desc" ? "asc" : "desc" }
      : { key, dir: key === "courseName" ? "asc" : "desc" }));
  };

  const sortValue = (row, key) => {
    if (key === "courseName") {
      return row.courseName ?? "";
    }
    if (key === "total") {
      return (row.dropoutCount ?? 0) + (row.delinquencyCount ?? 0);
    }
    return row[key] ?? 0;
  };

  const courses = data?.courses ?? [];
  const rows = [...courses].sort((a, b) => {
    const left = sortValue(a, sort.key);
    const right = sortValue(b, sort.key);
    const cmp = typeof left === "string" ? left.localeCompare(right, "pt-BR") : left - right;
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const maxByColumn = COLUMNS.reduce((acc, column) => ({
    ...acc,
    [column.key]: Math.max(1, ...courses.map((c) => c[column.key] ?? 0))
  }), {});

  const titleNode = (
    <div className={styles.titleRow}>
      <span>Risco por Curso • Evasão × Inadimplência</span>
      <div className={styles.infoAnchor}>
        <img
          src={InfoIcon}
          alt="Informação"
          className={styles.infoIcon}
          onClick={() => setShowPopover(!showPopover)}
        />
        {showPopover && (
          <div className={styles.popover}>
            <p>
              Número de alunos em risco por curso. <strong>Evasão</strong>: análise semanal de
              possíveis evasores, contando quem soma score 2 ou mais (dois ou mais critérios, ou um
              crítico). <strong>Inadimplência</strong>: modelo preditivo, contando a faixa de risco
              alto (probabilidade ≥ 60%) no lote pontuado nos últimos 14 dias.
              Cor mais forte = mais alunos naquela coluna; as duas colunas contam coisas diferentes
              e não se comparam entre si. Este card não responde ao período nem aos filtros da tela:
              os dois lados são snapshots, não recortes por data.
            </p>
            <button type="button" onClick={() => setShowPopover(false)}>Fechar</button>
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <FinanceCardChart code={code} title={titleNode} height="460px">
        <ZeroData>
          <h4>Carregando risco por curso...</h4>
        </ZeroData>
      </FinanceCardChart>
    );
  }

  if (!rows.length) {
    return (
      <FinanceCardChart code={code} title={titleNode} height="460px">
        <ZeroData>
          <h4>Análise preditiva ainda não executada para esta instituição.</h4>
        </ZeroData>
      </FinanceCardChart>
    );
  }

  const dropoutStamp = formatStamp(data?.dropoutAnalyzedAt);
  const delinquencyStamp = formatStamp(data?.delinquencyScoredAt);

  const sortIndicator = (key) => (sort.key === key ? (sort.dir === "asc" ? " ↑" : " ↓") : "");

  const detailColumns = drill ? DETAIL_COLUMNS[drill.risk] : [];

  return (
    <>
    <FinanceCardChart code={code} title={titleNode} height="460px">
      <div className={styles.heatmap}>
        <div className={styles.grid}>
          <button
            type="button"
            className={`${styles.headerCell} ${styles.courseHeader}`}
            onClick={() => toggleSort("courseName")}
          >
            Curso{sortIndicator("courseName")}
          </button>
          {COLUMNS.map((column) => (
            <button
              key={column.key}
              type="button"
              className={styles.headerCell}
              onClick={() => toggleSort(column.key)}
            >
              {column.label}{sortIndicator(column.key)}
              <span className={styles.headerCut}>{column.cut}</span>
            </button>
          ))}

          {rows.map((row) => (
            <React.Fragment key={row.courseName}>
              <div className={styles.courseCell} title={row.courseName}>{row.courseName}</div>
              {COLUMNS.map((column) => {
                const count = row[column.key] ?? 0;
                const label = `${count} aluno(s) em risco de ${column.noun} em ${row.courseName}`;

                // Célula zerada não abre nada — fica como div, sem foco nem cursor.
                if (!count) {
                  return (
                    <div
                      key={column.key}
                      className={`${styles.valueCell} ${styles.zeroCell}`}
                      style={cellStyle(count, maxByColumn[column.key])}
                      title={label}
                    >
                      {count}
                    </div>
                  );
                }

                const isSelected = drill?.courseName === row.courseName && drill?.risk === column.risk;

                return (
                  <button
                    key={column.key}
                    type="button"
                    className={`${styles.valueCell} ${styles.clickableCell} ${isSelected ? styles.selectedCell : ""}`}
                    style={cellStyle(count, maxByColumn[column.key])}
                    title={`${label} — clique para ver os alunos`}
                    aria-pressed={isSelected}
                    onClick={() => setDrill(isSelected ? null : {
                      courseName: row.courseName,
                      risk: column.risk,
                      label: column.label,
                      noun: column.noun,
                      count
                    })}
                  >
                    {count}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {(dropoutStamp || delinquencyStamp) && (
        <p className={styles.stamp}>
          Snapshot preditivo
          {dropoutStamp && ` • evasão em ${dropoutStamp}`}
          {delinquencyStamp && ` • inadimplência em ${delinquencyStamp}`}
        </p>
      )}
    </FinanceCardChart>

    {drill && (
      <div className={styles.detailCard}>
        <div className={styles.detailHeader}>
          <h4>
            {drill.label} • {drill.courseName}
            <span className={styles.detailCount}>
              {drill.count} aluno{drill.count === 1 ? "" : "s"} em risco de {drill.noun}
            </span>
          </h4>
          <button type="button" className={styles.detailClose} onClick={() => setDrill(null)}>
            Fechar
          </button>
        </div>

        {drillLoading ? (
          <p className={styles.detailEmpty}>Carregando alunos...</p>
        ) : !drillRows.length ? (
          <p className={styles.detailEmpty}>Nenhum aluno retornado para esta célula.</p>
        ) : (
          <div className={styles.detailTableWrapper}>
            <table className={styles.detailTable}>
              <thead>
                <tr>
                  {detailColumns.map((column) => <th key={column.label}>{column.label}</th>)}
                  <th>Motivos</th>
                </tr>
              </thead>
              <tbody>
                {drillRows.map((student) => (
                  <tr key={student.studentId}>
                    {detailColumns.map((column) => <td key={column.label}>{column.render(student)}</td>)}
                    <td>
                      {student.reasons?.length ? (
                        <ul className={styles.reasonList}>
                          {student.reasons.map((reason, index) => <li key={index}>{reason}</li>)}
                        </ul>
                      ) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )}
    </>
  );
};

export default CourseRiskHeatmap;
